import { generatePuzzle } from '../sudoku/generator';
import type { Difficulty, Puzzle } from '../sudoku/types';
import type { GenerateRequest, GenerateResponse } from './generator.worker';

/**
 * Rätselnachschub. Die Erzeugung läuft in einem Worker, damit die Oberfläche
 * nicht ruckelt; zusätzlich wird pro Schwierigkeitsgrad ein Rätsel vorgehalten,
 * sodass „Neues Spiel“ sofort startet.
 */

let worker: Worker | null = null;
let nextId = 1;
const pending = new Map<number, (result: Puzzle) => void>();
const prefetched = new Map<Difficulty, Puzzle>();
const inFlight = new Set<Difficulty>();

function getWorker(): Worker | null {
  if (worker) return worker;
  if (typeof Worker === 'undefined') return null;
  try {
    worker = new Worker(new URL('./generator.worker.ts', import.meta.url), { type: 'module' });
    worker.addEventListener('message', (event: MessageEvent<GenerateResponse>) => {
      const resolve = pending.get(event.data.id);
      if (!resolve) return;
      pending.delete(event.data.id);
      resolve({
        puzzle: Uint8Array.from(event.data.puzzle),
        solution: Uint8Array.from(event.data.solution),
        difficulty: event.data.difficulty,
        clues: event.data.clues,
        seed: event.data.seed,
      });
    });
    worker.addEventListener('error', () => {
      // Fällt auf die synchrone Erzeugung im Hauptthread zurück.
      worker = null;
      for (const [id, resolve] of pending) {
        pending.delete(id);
        resolve(generatePuzzle('medium'));
      }
    });
    return worker;
  } catch {
    return null;
  }
}

function generateViaWorker(difficulty: Difficulty, seed?: number): Promise<Puzzle> | null {
  const instance = getWorker();
  if (!instance) return null;
  const id = nextId++;
  const request: GenerateRequest = seed === undefined ? { id, difficulty } : { id, difficulty, seed };
  return new Promise<Puzzle>((resolve) => {
    pending.set(id, resolve);
    instance.postMessage(request);
  });
}

/** Liefert ein Rätsel – bevorzugt aus dem Vorrat, sonst frisch erzeugt. */
export async function requestPuzzle(difficulty: Difficulty, seed?: number): Promise<Puzzle> {
  if (seed === undefined) {
    const ready = prefetched.get(difficulty);
    if (ready) {
      prefetched.delete(difficulty);
      void prefetch(difficulty);
      return ready;
    }
  }
  const viaWorker = generateViaWorker(difficulty, seed);
  const puzzle = viaWorker ? await viaWorker : generatePuzzle(difficulty, seed === undefined ? {} : { seed });
  if (seed === undefined) void prefetch(difficulty);
  return puzzle;
}

/** Erzeugt im Hintergrund ein Rätsel auf Vorrat. */
export async function prefetch(difficulty: Difficulty): Promise<void> {
  if (prefetched.has(difficulty) || inFlight.has(difficulty)) return;
  const viaWorker = generateViaWorker(difficulty);
  if (!viaWorker) return; // Ohne Worker nicht vorab erzeugen, das würde die UI blockieren.
  inFlight.add(difficulty);
  try {
    prefetched.set(difficulty, await viaWorker);
  } finally {
    inFlight.delete(difficulty);
  }
}
